import React from 'react';

export default function PrivacyScreen() {
  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <span style={styles.logo}>📱</span>
          <h1 style={styles.brand}>Peekr</h1>
        </div>

        <h2 style={styles.title}>Privacy Policy</h2>
        <p style={styles.updated}>Last updated: November 2026</p>

        <Section title="Overview">
          Peekr is a real-time sharing app that lets hosts broadcast photos, videos, and documents
          to viewers via a browser — no viewer account required. We are committed to protecting
          your privacy and being transparent about how we handle your data.
        </Section>

        <Section title="Information We Collect">
          <b style={styles.b}>Content you share:</b> Photos, videos, and documents you share during
          a session are uploaded to Cloudinary (our media storage provider) for delivery
          to viewers. Files are retained by Cloudinary and subject to their retention policies.
          Peekr does not currently implement automatic deletion of files from Cloudinary after a session ends.
          <br /><br />
          <b style={styles.b}>Session data:</b> We store temporary room codes and viewer counts in
          memory on our server only for the duration of an active session. This data is deleted
          immediately when the session ends.
          <br /><br />
          <b style={styles.b}>Purchase data:</b> Subscription purchases are processed by Apple
          (App Store) and managed by RevenueCat. Peekr does not store payment details. RevenueCat
          stores anonymised purchase records to manage your subscription status.
          <br /><br />
          <b style={styles.b}>App identifiers:</b> RevenueCat uses an app-specific customer identifier
          to track subscription entitlements. Peekr issues private installation credentials to
          authorize host access. These identifiers are not your Apple password.
          <br /><br />
          <b style={styles.b}>Free-preview usage:</b> To provide 2 included live showings and 3 AI
          generations without a Peekr login, we verify an Apple-signed app download transaction.
          We store a hashed App Store transaction identity and usage counters on our server, so
          reinstalling the app with the same Apple Account does not reset the allowance. We do
          not receive your Apple password or Apple Account email from this transaction.
          <br /><br />
          <b style={styles.b}>Buyer contact details:</b> Buyers enter their name and phone or email
          before joining. These details are shared with the showing host and retained on our
          server for up to 7 days. Shared photo galleries remain available for up to 48 hours.
        </Section>

        <Section title="Information We Do Not Collect">
          Peekr does not require a separate host or buyer account registration. Buyers do provide
          contact details to the host as described above. We do not collect Apple passwords or
          payment card details. We do not track your location. We do not use
          advertising or analytics SDKs. We do not sell or share your data with third parties
          for marketing purposes.
        </Section>

        <Section title="Third-Party Services">
          Peekr uses the following third-party services:
          <br /><br />
          <b style={styles.b}>Anthropic</b> — optional AI generation. Before using AI tools,
          the app asks permission to send selected property photos, supplied listing details,
          and relevant document content to Anthropic. Do not share content containing someone
          else’s personal information without their permission. Walkthrough preparation uses
          sampled photos and listing details; its video and PDF prompts use item titles rather
          than analysis of those files. Review generated facts before using them. You can turn
          AI sharing off from Privacy &amp; AI sharing on the app’s home screen. Manual tools
          remain available. Anthropic’s privacy policy: anthropic.com/legal/privacy<br /><br />
          <b style={styles.b}>LiveKit</b> — live video and audio delivery between hosts and
          invited buyers. Privacy policy: livekit.io/privacy-policy<br /><br />
          <b style={styles.b}>Cloudinary</b> — media storage and delivery. Files uploaded during
          sessions are stored temporarily. Cloudinary's privacy policy: cloudinary.com/privacy<br /><br />
          <b style={styles.b}>RevenueCat</b> — subscription management. Privacy policy:
          revenuecat.com/privacy<br /><br />
          <b style={styles.b}>Railway</b> — server infrastructure. Session data passes through
          Railway's servers. Persistent galleries, leads, private installation credentials, and
          preview counters are stored using Upstash Redis. Privacy policies: railway.app/legal/privacy
          and upstash.com/trust/privacy.pdf
        </Section>

        <Section title="Data Retention">
          Private saved listings, their media references, AI results and review drafts are kept
          until you delete the listing or request deletion. Deleting protected PDF/video media
          removes those assets; other uploaded photo files may remain in media storage until
          separately removed. Contact info@ebenova.net for help with data deletion.
          <br /><br />
          Session content (photos, videos, documents) uploaded during a session is stored
          by Cloudinary and subject to their retention policies. Peekr does not currently
          implement automatic deletion of media files from Cloudinary. Session room data is held in server memory only and deleted the moment
          a session closes. Subscription status is retained by RevenueCat as long as you have
          an active or lapsed subscription.
          Minimal hashed App Store identities and preview-use records are retained to prevent
          repeated free previews after reinstalling. Cached AI request results used for safe
          retries expire after 48 hours.
        </Section>

        <Section title="Children's Privacy">
          Peekr is not directed at children under 13. We do not knowingly collect personal
          information from children. If you believe a child has shared personal information
          through Peekr, contact us and we will delete it promptly.
        </Section>

        <Section title="Your Rights">
          Contact us with requests concerning the information described in this policy.
          If you have purchased a subscription and wish to
          manage or delete your RevenueCat data, you can do so through your Apple ID settings
          or by contacting us.
        </Section>

        <Section title="Changes to This Policy">
          We may update this policy as Peekr evolves. Material changes will be noted on this
          page with an updated date. Continued use of Peekr after changes constitutes acceptance.
        </Section>

        <Section title="Contact">
          Questions about this policy? Contact us at:<br /><br />
          <b style={styles.b}>Ebenova Solutions</b><br />
          privacy@getpeekr.com
        </Section>

        <div style={styles.footer}>
          <a href="/" style={styles.link}>← Back to Peekr</a>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      <p style={styles.sectionBody}>{children}</p>
    </div>
  );
}

const styles = {
  page: { backgroundColor: '#050C1A', minHeight: '100vh', color: '#FFFFFF', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  container: { maxWidth: 680, margin: '0 auto', padding: '48px 24px' },
  header: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 40 },
  logo: { fontSize: 32 },
  brand: { fontSize: 28, fontWeight: 800, color: '#FFFFFF', letterSpacing: 2, margin: 0 },
  title: { fontSize: 32, fontWeight: 700, color: '#FFFFFF', marginBottom: 8 },
  updated: { fontSize: 13, color: '#3A5570', marginBottom: 40 },
  section: { marginBottom: 36, borderLeft: '2px solid #122038', paddingLeft: 20 },
  sectionTitle: { fontSize: 16, fontWeight: 700, color: '#00D4FF', marginBottom: 10, letterSpacing: 1 },
  sectionBody: { fontSize: 14, color: '#6A85A0', lineHeight: 1.8, margin: 0 },
  b: { color: '#FFFFFF', fontWeight: 600 },
  footer: { marginTop: 60, paddingTop: 24, borderTop: '1px solid #122038' },
  link: { color: '#00D4FF', textDecoration: 'none', fontSize: 14 },
};
